import { useMemo, useState, useEffect, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CheckCheck, ChevronRight, Inbox, Loader } from 'lucide-react';
import { NotificationsListSkeleton } from '../../components/ui/Skeleton';
import { PageContainer, PageToolbar, EmptyState, ToolbarDivider } from '../../components/ui/PageLayout';
import { cn } from '../../components/ui/uiTheme';
import {
  NOTIFICATIONS_PAGE_SIZE,
  useNotificationsQueries,
} from './useNotificationsQueries';
import {
  NOTIFICATION_INBOXES,
  formatNotificationDate,
  getNotificationNavigatePath,
  inboxLabelForType,
  notificationTypeLabel,
  type AppNotification,
  type NotificationInboxId,
} from './notificationInboxUtils';
import ServerTablePagination from '../Dashboard/ServerTablePagination';

type ReadFilter = 'all' | 'unread';

function NotificationRow({
  notification,
  marking,
  onMarkRead,
  onOpen,
}: {
  notification: AppNotification;
  marking: boolean;
  onMarkRead: (id: string) => void;
  onOpen: (notification: AppNotification) => void;
}) {
  const navigatePath = getNotificationNavigatePath(notification);
  const isClickable = Boolean(navigatePath);
  const isUnread = !notification.readAt;

  return (
    <article
      className={cn(
        'group relative overflow-hidden rounded-xl border bg-dark-surface transition-colors',
        isUnread
          ? 'border-dark-border-glow bg-dark-elevated/30'
          : 'border-dark-border hover:border-dark-border-glow/70',
        isClickable && 'cursor-pointer hover:bg-dark-elevated/40'
      )}
      {...(isClickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onOpen(notification),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(notification);
              }
            },
          }
        : {})}
    >
      {isUnread ? (
        <span
          className="absolute inset-y-0 left-0 w-1 bg-vivid-red"
          aria-hidden
        />
      ) : null}

      <div className="px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-brand-purple/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-brand-purple-light">
                {inboxLabelForType(notification.notificationType)}
              </span>
              <span className="rounded-md bg-dark-base/80 px-2 py-0.5 text-[11px] font-medium text-slate-400">
                {notificationTypeLabel(notification.notificationType)}
              </span>
              {notification.repositoryFullName ? (
                <span className="truncate text-xs text-slate-500">
                  {notification.repositoryFullName}
                </span>
              ) : null}
              <span className="ml-auto shrink-0 text-xs text-slate-500">
                {formatNotificationDate(notification.createdAt)}
              </span>
            </div>

            <h3 className="mt-2 text-sm font-semibold text-slate-100">{notification.title}</h3>
            {notification.body ? (
              <p className="mt-1 line-clamp-3 text-sm leading-relaxed text-slate-400 whitespace-pre-wrap break-words">
                {notification.body}
              </p>
            ) : null}

            {isClickable ? (
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 transition-colors group-hover:text-slate-300">
                Open
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            ) : null}
          </div>

          {isUnread ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(notification.id);
              }}
              disabled={marking}
              className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-dark-base hover:text-vivid-red disabled:opacity-50"
              title="Mark as read"
              aria-label="Mark as read"
            >
              {marking ? (
                <Loader className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Check className="h-4 w-4" aria-hidden />
              )}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const [activeInbox, setActiveInbox] = useState<NotificationInboxId>('all');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [page, setPage] = useState(0);
  const [markingId, setMarkingId] = useState<string | null>(null);

  useEffect(() => {
    setPage(0);
  }, [activeInbox, readFilter]);

  const {
    notifications,
    total,
    unreadCount,
    inboxUnreadCounts,
    loading,
    markReadMutation,
    markAllReadMutation,
  } = useNotificationsQueries({
    inboxId: activeInbox,
    readFilter,
    page,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
  });

  const inboxMeta = useMemo(
    () => NOTIFICATION_INBOXES.find((inbox) => inbox.id === activeInbox) ?? NOTIFICATION_INBOXES[0],
    [activeInbox]
  );

  const offset = page * NOTIFICATIONS_PAGE_SIZE;

  const handleMarkRead = async (id: string) => {
    setMarkingId(id);
    try {
      await markReadMutation.mutateAsync(id);
    } finally {
      setMarkingId(null);
    }
  };

  const handleOpen = async (notification: AppNotification) => {
    const path = getNotificationNavigatePath(notification);
    if (!path) return;
    if (!notification.readAt) {
      await handleMarkRead(notification.id);
    }
    navigate(path);
  };

  const emptyTitle =
    readFilter === 'unread'
      ? `No unread ${activeInbox === 'all' ? '' : `${inboxMeta.label.toLowerCase()} `}notifications`
      : `No ${activeInbox === 'all' ? '' : `${inboxMeta.label.toLowerCase()} `}notifications yet`;

  return (
    <PageContainer>
      <PageToolbar
        title="Notifications"
        description="Browse updates by inbox — engineering alerts, HR modules, and more."
      >
        {unreadCount > 0 ? (
          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="btn-secondary-mdl inline-flex items-center gap-2"
          >
            <CheckCheck className="h-4 w-4" aria-hidden />
            Mark all read
          </button>
        ) : null}
      </PageToolbar>
      <ToolbarDivider />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <nav
          className="card-mdl-static shrink-0 lg:w-60"
          aria-label="Notification inboxes"
        >
          <div className="border-b border-dark-border px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inboxes</p>
          </div>
          <ul className="flex gap-1 overflow-x-auto p-2 lg:flex-col lg:overflow-visible">
            {NOTIFICATION_INBOXES.map((inbox) => {
              const Icon = inbox.icon;
              const unread = inboxUnreadCounts[inbox.id];
              const isActive = activeInbox === inbox.id;
              return (
                <li key={inbox.id} className="shrink-0 lg:shrink">
                  <button
                    type="button"
                    onClick={() => setActiveInbox(inbox.id)}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'flex min-w-[9.5rem] items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors lg:min-w-0 lg:w-full',
                      isActive
                        ? 'bg-brand-purple/15 text-slate-100'
                        : 'text-slate-400 hover:bg-dark-elevated/70 hover:text-slate-200'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        isActive ? 'bg-brand-purple/20 text-brand-purple-light' : 'bg-dark-base/80'
                      )}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{inbox.label}</span>
                    </span>
                    {unread > 0 ? (
                      <span className="rounded-full bg-vivid-red/15 px-2 py-0.5 text-[10px] font-semibold text-vivid-red">
                        {unread}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <section className="min-w-0 flex-1">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">{inboxMeta.label}</h2>
              <p className="text-sm text-slate-500">{inboxMeta.description}</p>
            </div>

            <div className="inline-flex overflow-hidden rounded-xl border border-dark-border">
              <button
                type="button"
                onClick={() => setReadFilter('all')}
                className={cn(
                  'px-4 py-2 text-sm font-medium transition-colors',
                  readFilter === 'all'
                    ? 'bg-brand-purple/15 text-slate-100'
                    : 'bg-dark-surface text-slate-400 hover:text-slate-200'
                )}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setReadFilter('unread')}
                className={cn(
                  'border-l border-dark-border px-4 py-2 text-sm font-medium transition-colors',
                  readFilter === 'unread'
                    ? 'bg-brand-purple/15 text-slate-100'
                    : 'bg-dark-surface text-slate-400 hover:text-slate-200'
                )}
              >
                Unread
                {inboxUnreadCounts[activeInbox] > 0 ? ` (${inboxUnreadCounts[activeInbox]})` : ''}
              </button>
            </div>
          </div>

          {loading ? (
            <NotificationsListSkeleton count={5} />
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-10 w-10 text-slate-500" aria-hidden />}
              title={emptyTitle.trim()}
              description={
                activeInbox === 'all'
                  ? 'Notifications appear here for engineering alerts, HR modules, suggestion box activity, and more.'
                  : `When you receive ${inboxMeta.label.toLowerCase()} notifications, they will show up here.`
              }
            />
          ) : (
            <div className="card-mdl-static overflow-hidden">
              <ul className="divide-y divide-dark-border">
                {notifications.map((notification) => (
                  <li key={notification.id} className="p-1 sm:p-2">
                    <NotificationRow
                      notification={notification}
                      marking={markingId === notification.id}
                      onMarkRead={handleMarkRead}
                      onOpen={handleOpen}
                    />
                  </li>
                ))}
              </ul>
              <ServerTablePagination
                total={total}
                limit={NOTIFICATIONS_PAGE_SIZE}
                offset={offset}
                onPageChange={(nextOffset) => setPage(Math.floor(nextOffset / NOTIFICATIONS_PAGE_SIZE))}
                idPrefix="notifications"
              />
            </div>
          )}
        </section>
      </div>
    </PageContainer>
  );
}
